import _core from "./timecodeCore.js";
const core = _core;
const rationalFrameRate = core.rationalFrameRate;

/**
 * Wall-clock seconds from HH:MM:SS:FF (ignores drop-frame math).
 * Replaces node-timecodes.toSeconds for clock-time paths.
 */
function clockTcToSeconds(tc, frameRate) {
  const parts = String(tc).replace(/;|\./g, ":").split(":");
  const hours = parseInt(parts[0], 10) || 0;
  const minutes = parseInt(parts[1], 10) || 0;
  const seconds = parseInt(parts[2], 10) || 0;
  const frames = parseInt(parts[3], 10) || 0;
  const rate = parseFloat(frameRate);
  return hours * 3600 + minutes * 60 + seconds + frames / rate;
}

/**
 * Wall-clock HH:MM:SS:FF from seconds (ignores drop-frame math).
 * Replaces previous secToTcClock implementation.
 */
function secondsToClockTc(sec, frameRate) {
  sec = Math.max(0, parseFloat(sec));
  frameRate = parseFloat(frameRate);
  let totalSecs = Math.floor(sec);
  let frames = Math.round((sec - totalSecs) * frameRate);
  if (frames >= Math.round(frameRate)) {
    frames = 0;
    totalSecs += 1;
  }
  const hours = Math.floor(totalSecs / 3600);
  const minutes = Math.floor(totalSecs % 3600 / 60);
  const secs = totalSecs % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}:${frames.toString().padStart(2, "0")}`;
}

/**
 * node-timecodes.fromSeconds-style output used by tcValidate for bare integers.
 */
function clockFromSeconds(seconds, frameRate) {
  return secondsToClockTc(seconds, frameRate);
}
export default {
  parseTcToSec: function (tc, frameRate, dropFrame, useClockTime) {
    try {
      if (useClockTime === undefined) {
        console.log("ERROR MISSING USECLOCKTIME FLAG");
      }
      if (tc.match(/\d{2}:\d{2}:\d{2}:\d{2}|\d{2}:\d{2}:\d{2};\d{2}/)) {
        /* TC SMPTE */
        return useClockTime ? this.tcClockToSec(tc, frameRate, dropFrame) : this.tcToSec(tc, frameRate, dropFrame);
      } else if (tc.match(/\d{2}:\d{2}:\d{2}\.\d{1,3}|\d{2}:\d{2}:\d{2}\,\d{1,3}/)) {
        /* TC MS */
        return this.tcMsToSec(tc, frameRate, dropFrame);
      } else if (/^\d{2}:\d{2}:\d{2}$/.test(tc)) {
        /* TC MS missing fractional part (e.g. 00:03:01 → 00:03:01.000) */
        return this.tcMsToSec(tc + ".000", frameRate, dropFrame);
      } else if (tc.match(/\d+\.?\d*s/i)) {
        return parseFloat(tc.match(/\d+\.?\d*/)[0]);
      } else if (/^\d{8}$/.test(tc)) {
        //8 digit timecode
        const hours = tc.slice(0, 2);
        const minutes = tc.slice(2, 4);
        const seconds = tc.slice(4, 6);
        const frames = tc.slice(6, 8);
        const separator = frameRate == 29.97 || frameRate == 59.94 ? ";" : ":";
        return useClockTime ? this.tcClockToSec(`${hours}:${minutes}:${seconds}${separator}${frames}`, frameRate) : this.tcToSec(`${hours}:${minutes}:${seconds}${separator}${frames}`, frameRate);
      } else if (tc.match(/\d+f/i)) {
        return this.framesToSec(tc.match(/\d+/)[0], frameRate);
      }
    } catch (err) {
      return 0;
    }
  },
  // clockTimeFlag indicates the tc is already in clock time and therefore represents real (wall clock) time
  tcToSec: function (tc, frameRate, dropFrame, clockTimeFlag = false) {
    try {
      if (clockTimeFlag) {
        return clockTcToSeconds(tc, frameRate);
      }
      frameRate = parseFloat(frameRate);
      const df = core.resolveDropFrame(frameRate, dropFrame);
      let tcObj;
      try {
        tcObj = core.create(tc.replace(/;|\./g, ":"), frameRate, df);
        // Validate frame field is in range for this rate; mirror previous parse failure path
        const parts = String(tc).replace(/;|\./g, ":").split(":");
        const frs = parseInt(parts[3], 10);
        const ifps = core.intFramerate(frameRate);
        if (frs >= ifps) {
          throw new Error("Frame number is higher than the frame rate");
        }
        if (df) {
          const mins = parseInt(parts[1], 10);
          const secs = parseInt(parts[2], 10);
          const dropN = frameRate > 30 ? 4 : 2;
          if (secs === 0 && mins % 10 !== 0 && frs < dropN) {
            throw new Error("Drop frame timecode cannot have this frame number");
          }
        }
      } catch (parseErr) {
        tcObj = this.createTc(tc, frameRate, df);
      }
      return core.frameCountToSeconds(tcObj.frameCount, frameRate);
    } catch (err) {
      console.log(err.message);
      if (tc) {
        return this.parseTcToSec(tc, frameRate, dropFrame, !clockTimeFlag);
      }
      return;
    }
  },
  tcClockToSec: function (tc, frameRate) {
    return clockTcToSeconds(tc, frameRate);
  },
  tcTicksToSec: function (tc) {
    let sec;
    let tcParts = tc.trim().replace(/:/gm, " ").split(" ");
    sec = tcParts[0] * 3600; //Hours
    sec += tcParts[1] * 60; //Minutes
    sec += parseInt(tcParts[2]); //Seconds

    sec += Number(Math.fround(tcParts[3] * 4 / 1000).toFixed(3)); ////HH:MM:SS:TTT A “tick” is defined as 4 msec and has a range of 0 to 249.

    return sec;
  },
  secToTicksSec: function (sec, frameRate, dropFrame) {
    let tcMs = this.secToTcMs(sec, frameRate, dropFrame);
    let ms = tcMs.split(",")[1];
    let ticks = parseInt(ms / 4);
    return tcMs.split(",")[0] + ":" + ticks.toString().padStart(3, "0");
  },
  tcFractionToSec: function (tc) {
    let sec;
    let tcParts = tc.trim().replace(/:/gm, " ").split(" ");
    sec = tcParts[0] * 3600; //Hours
    sec += tcParts[1] * 60; //Minutes
    sec += parseFloat(tcParts[2]); //Seconds
    return Math.fround(sec);
  },
  secToTc: function (sec, frameRate, dropFrame) {
    if (dropFrame === undefined) {
      dropFrame = true;
    }
    frameRate = parseFloat(frameRate);
    const df = core.resolveDropFrame(frameRate, dropFrame);
    const frameCount = core.secondsToFrameCount(sec, frameRate);
    return core.frameCountToTcString(frameCount, frameRate, df, false);
  },
  secToTcClock: function (sec, frameRate, dropFrame) {
    return secondsToClockTc(sec, frameRate);
  },
  secToTcMs: function (sec, frameRate, dropFrame) {
    sec = Math.max(0, parseFloat(sec) || 0);

    // Calculate hours, minutes, seconds, and milliseconds
    let hours = Math.floor(sec / 3600);
    let minutes = Math.floor(sec % 3600 / 60);
    let secs = Math.floor(sec % 60);
    let milliseconds = Math.round(sec % 1 * 1000);
    if (milliseconds == "1000") {
      milliseconds = "999";
    }

    // Format with leading zeros
    let hh = hours.toString().padStart(2, "0");
    let mm = minutes.toString().padStart(2, "0");
    let ss = secs.toString().padStart(2, "0");
    let mmm = milliseconds.toString().padStart(3, "0");

    // Build timecode string
    const timecode = `${hh}:${mm}:${ss},${mmm}`;
    return timecode;
  },
  secToFrames: function (sec, frameRate, dropFrame) {
    return core.secondsToFrameCount(sec, frameRate);
  },
  tcToFrames: function (tcString, frameRate, dropFrame) {
    frameRate = parseFloat(frameRate);
    const df = core.resolveDropFrame(frameRate, dropFrame);
    return core.create(tcString, frameRate, df).frameCount;
  },
  frameToTc: function (frames, frameRate, dropFrame) {
    frameRate = parseFloat(frameRate);
    const df = core.resolveDropFrame(frameRate, dropFrame);
    return core.create(frames, frameRate, df).toString();
  },
  tcAdd: function (tcString, frameRate, dropFrame, value) {
    frameRate = parseFloat(frameRate);
    const df = core.resolveDropFrame(frameRate, dropFrame);
    let tc = core.create(tcString, frameRate, df);
    tc.add(value);
    return tc.toString();
  },
  tcSubtract: function (tcString, frameRate, dropFrame, value) {
    frameRate = parseFloat(frameRate);
    const df = core.resolveDropFrame(frameRate, dropFrame);
    let tc = core.create(tcString, frameRate, df);
    tc.subtract(value);
    return tc.toString();
  },
  msToFrame: function (ms, frameRate) {
    return Math.floor(ms / 1000 * frameRate);
  },
  tcMsToSec: function (tcMs, frameRate, dropFrame) {
    let sec;
    let tcParts = (tcMs + "000").substring(0, 12).trim().replace(/,|\.|;|:/gm, " ").split(" ");
    sec = tcParts[0] * 3600; //Hours

    sec += tcParts[1] * 60; //Minutes

    sec += parseInt(tcParts[2]); //Seconds

    sec += tcParts[3] / 1000; //Ms

    return sec;
  },
  tcMsToTc: function (tcMs, frameRate, dropFrame) {
    let sec = this.tcMsToSec(tcMs, frameRate, dropFrame);
    return this.secToTc(sec, frameRate, dropFrame);
  },
  //For quickTimeText format
  tcQuickTimeToSec: function (tc, timeScaleValue) {
    let sec;
    let tcParts = tc.trim().replace(/,|\.|;|:/gm, " ").split(" ");
    sec = tcParts[0] * 3600; //Hours
    sec += tcParts[1] * 60; //Minutes
    sec += parseInt(tcParts[2]); //Seconds
    sec += tcParts[3] / timeScaleValue; //timescale
    return sec;
  },
  centisecToSeconds: function (tcFr) {
    let sec;
    let tcParts = tcFr.trim().replace(/,|\.|;|:/gm, " ").split(" ");
    sec = tcParts[0] * 3600; //Hours
    sec += tcParts[1] * 60; //Minutes
    sec += parseInt(tcParts[2]); //Seconds
    sec += parseInt(tcParts[3]) / 100; //centisec

    return sec;
  },
  framesToMs: function (frames, frameRate) {
    return Math.floor(frames / frameRate * 1000);
  },
  framesToSec: function (frames, frameRate = 29.97) {
    return core.frameCountToSeconds(parseFloat(frames), frameRate);
  },
  framesToTc: function (frames, frameRate, dropFrame = true) {
    frameRate = parseFloat(frameRate);
    const df = core.resolveDropFrame(frameRate, dropFrame);
    return core.create(parseInt(frames, 10), frameRate, df).toString();
  },
  createTc: function (tc, frameRate, dropFrame = false) {
    frameRate = parseFloat(frameRate);
    const df = core.resolveDropFrame(frameRate, dropFrame);
    try {
      const normalized = tc.toString().replace(";", ":");
      const parts = normalized.split(":");
      const frs = parseInt(parts[3], 10);
      const ifps = core.intFramerate(frameRate);
      if (frs >= ifps) {
        throw new Error("Frame number is higher than the frame rate");
      }
      if (df) {
        const mins = parseInt(parts[1], 10);
        const secs = parseInt(parts[2], 10);
        const dropN = frameRate > 30 ? 4 : 2;
        if (secs === 0 && mins % 10 !== 0 && frs < dropN) {
          throw new Error("Drop frame timecode cannot have this frame number");
        }
      }
      return core.create(normalized, frameRate, df);
    } catch (e) {
      if (df && tc.slice(-2) == "00") {
        return core.create(tc.slice(0, 9) + (frameRate > 30 ? "04" : "02"), frameRate, df);
      } else if (parseInt(tc.slice(-2)) >= Math.floor(frameRate)) {
        let fixedTc = tc.slice(0, 9) + (Math.floor(frameRate) - (frameRate === 23.976 ? 0 : 1)).toString().padStart(2, "0");
        return core.create(fixedTc, frameRate, df);
      } else {
        try {
          let tcNonDrop;
          if (tc.slice(-2) == "00" && df) {
            return core.create(tc.slice(0, 8) + ";05", frameRate, df);
          } else if (parseFloat(frameRate) === 59.94) {
            tcNonDrop = core.create(tc, 60, false);
            tcNonDrop.add(4);
          } else {
            tcNonDrop = core.create(tc, 30, false);
            tcNonDrop.add(2);
          }
          return core.create(tcNonDrop.toString(), frameRate, df);
        } catch (e2) {
          throw new Error("Invalid Timecode: " + e2.message);
        }
      }
    }
  },
  formatTcMs: function (tcMs) {
    return tcMs.substring(0, 8) + "," + tcMs.substring(9, 12);
  },
  formatTimecodeString: function (tc, dropFrame, encodeOption = "auto") {
    if (encodeOption === "auto") {
      if (dropFrame) {
        return tc.substring(0, 8) + ";" + tc.substring(9);
      } else {
        return tc.replace(/;/g, ":");
      }
    } else if (encodeOption === "colon") {
      return tc.replace(/;/g, ":");
    } else {
      return tc.substring(0, 8) + ";" + tc.substring(9);
    }
  },
  tcValidate: function (tc, frameRate, dropFrame) {
    tc = tc.trim().replace(/;/g, ":").toUpperCase();
    try {
      if (/^\d\d:\d\d:\d\d:\d\d$/g.test(tc)) {
        return tc;
      } else if (/^\d\d\d\d\d\d\d\d$/g.test(tc)) {
        return this.createTc(tc.match(/.{1,2}/g).join(":"), frameRate, dropFrame).toString();
      } else if (/^\d+[HMSF]$/gmi.test(tc)) {
        if (tc.indexOf("H") > -1) {
          var hours = parseInt(tc.split("H")[0]);
          if (hours > 23) {
            return "23:00:00:00";
          } else if (hours < 10) {
            return "0" + hours + ":00:00:00";
          } else {
            return hours + ":00:00:00";
          }
        } else if (tc.indexOf("M") > -1) {
          var minutes = parseInt(tc.split("M")[0]);
          if (minutes > 59) {
            return "00:59:00:00";
          } else if (minutes < 10) {
            return "00:0" + minutes + ":00:00";
          } else {
            return "00:" + minutes + ":00:00";
          }
        } else if (tc.indexOf("S") > -1) {
          var seconds = parseInt(tc.split("S")[0]);
          if (seconds > 59) {
            return "00:00:59:00";
          } else if (seconds < 10) {
            return "00:00:0" + seconds + ":00";
          } else {
            return "00:00:" + seconds + ":00";
          }
        } else {
          var frames = parseInt(tc.split("F")[0]);
          if (frames > parseInt(frameRate)) {
            return "00:00:00:" + parseInt(frameRate);
          } else if (frames < 10) {
            return "00:00:00:0" + frames;
          } else {
            return "00:00:00:" + frames;
          }
        }
      } else if (/^\d+$/g.test(tc)) {
        var seconds = tc / frameRate;
        return clockFromSeconds(seconds, frameRate);
      } else {
        return "00:00:00:00";
      }
    } catch (e) {
      return "";
    }
  },
  //xml formats time parsing
  parseColonTimeWithFrames_: function (tc, rateInfo, regex) {
    const results = regex.exec(tc);
    const hours = Number(results[1]);
    const minutes = Number(results[2]);
    let seconds = Number(results[3]);
    let frames = Number(results[4]);
    const subframes = Number(results[5]) || 0;
    frames += subframes / rateInfo.subFrameRate;
    seconds += frames / rateInfo.frameRate;
    return seconds + minutes * 60 + hours * 3600;
  },
  parseTimeFromRegex_: function (tc, regex) {
    const results = regex.exec(tc);
    if (results == null || results[0] == '') {
      return null;
    }
    // This capture is optional, but will still be in the array as undefined,
    // in which case it is 0.
    const hours = Number(results[1]) || 0;
    const minutes = Number(results[2]) || 0;
    const seconds = Number(results[3]) || 0;
    const milliseconds = Number(results[4]) || 0;
    return milliseconds / 1000 + seconds + minutes * 60 + hours * 3600;
  },
  parseFramesTime_: function (tc, rateInfo, regex) {
    // 75f or 75.5f
    const results = regex.exec(tc);
    const frames = Number(results[1]);
    return frames / rateInfo.frameRate;
  },
  parseTickTime_: function (tc, rateInfo, regex) {
    // 50t or 50.5t
    const results = regex.exec(tc);
    const ticks = Number(results[1]);
    return ticks / rateInfo.tickRate;
  }
};